import { useEffect, useState } from 'react';
import { getUserQuoteById } from "../../../lib/quotes";

export const useConnectionData = (user) => {
  const [connectionData, setConnectionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        if (!user?.uid) {
          setConnectionData([]);
          setLoading(false);
          return;
        }

        const CACHE_KEY = `user_quotes_${user.uid}`;
        const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes in ms 

        // Check localStorage for cached data
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data: cachedData, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_EXPIRY) {
              // Cache valid: use it immediately
              setConnectionData(cachedData);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.warn('Invalid cached data, fetching fresh:', err);
          }
        }

        // No valid cache: fetch fresh
        const res = await getUserQuoteById(user.uid);
        
        if (mounted && res) {
          const transformedData = res
            // First filter based on MyShipment logic (exclude "pending" and "decline" statuses)
            .filter(doc => doc.status !== "pending" && doc.status !== "decline")
            // Then map and apply subStatus-based filtering
            .map(doc => {
              const subStatus = String(doc.orderDetails?.subStatus);
              return {
                fromCountry: String(doc.loadCountry || ""),
                toCountry: String(doc.unloadCountry || ""),
                status: subStatus,
                // Add color based on subStatus
                color: statusColors[subStatus] ?? statusColors['Collection']
              };
            })
            .filter(item => 
              item.fromCountry && 
              item.toCountry && 
              item.status !== "Delivered" && 
              item.status !== "Unknown"
            ); // Filter out invalid entries and unwanted subStatuses
          
          setConnectionData(transformedData);
          
          // Cache the transformed data
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ data: transformedData, timestamp: Date.now() })
          );
          
          // console.log(transformedData, "Processed connection data");
        }
      } catch (err) {
        console.error("Error fetching connection data:", err);
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [user?.uid]); // Only depend on uid to prevent unnecessary re-renders

  return { connectionData, loading, error };
};

export const statusColors = {
  'Collection': 0x1C58F4,       // Blue (Crayola)
  'Compliance': 0x00944B,       // Spanish Green
  'In Transit': 0xFBC337,       // Saffron
  'Custom Clearance': 0xFF6A24, // Orange
  'Final Delivery': 0x0064FA,   // Dark Violet (using hex from screenshot)
  // 'Delivered': 0xFF1D1B       // Red (RYB)
};